import { formatUzPhone } from '@qm/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { ReportSheet } from '../components/ReportSheet';
import { Screen } from '../components/layout';
import { FavoriteButton, ListingGrid, PriceText } from '../components/listing';
import { Button, EmptyState, ErrorState, Field, Sheet, Spinner, errorMessage, formatDate, useRelativeDate, useToast } from '../components/ui';
import { ApiError, api, type ListingDetail } from '../lib/api';
import { useMe } from '../lib/auth';
import { canShareMessage, confirmDialog, haptic, openTelegramLink, shareMessage, shareToTelegram } from '../lib/telegram';
import { useContactOptions, useListing, useListingAction, usePromotionTariffs, useSimilar, useStartChat } from '../lib/queries';
import { PromoteSheet } from '../components/PromoteSheet';
import { RatingLine, useMemberFor } from '../components/reviews';
import { SpecTiles, splitAttributes } from '../components/specs';
import { ListingPlaceholder } from '../components/placeholder';
import { Avatar } from '../components/Avatar';
import { shortPlaces } from '../lib/format';

function Gallery({ images, title, actions, categoryId }: { images: ListingDetail['images']; title: string; actions?: React.ReactNode; categoryId?: string | null }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  if (!images.length) {
    return (
      <div className="gallery gallery-empty">
        <ListingPlaceholder categoryId={categoryId} large />
        <span className="gallery-nophoto tiny">{t('listing.noPhotos')}</span>
        {actions ? <div className="gallery-actions">{actions}</div> : null}
      </div>
    );
  }
  return (
    <div className="gallery">
      {actions ? <div className="gallery-actions">{actions}</div> : null}
      <div
        className="gallery-track"
        onScroll={(e) => {
          const el = e.currentTarget;
          setIndex(Math.round(el.scrollLeft / el.clientWidth));
        }}
      >
        {images.map((img, i) => (
          <img key={img.id} src={img.url} alt={i === 0 ? title : ''} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" width={img.width} height={img.height} />
        ))}
      </div>
      {images.length > 1 ? (
        <span className="gallery-counter">
          {index + 1}/{images.length}
        </span>
      ) : null}
    </div>
  );
}

export function ListingPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rel = useRelativeDate();
  const memberFor = useMemberFor();
  const toast = useToast();
  const { data: l, isLoading, error, refetch } = useListing(id);
  const similar = useSimilar(l?.status === undefined || l?.status === 'active' ? id : undefined);
  // «Поделиться»: в Telegram — карточка объявления (фото, цена, место, кнопка «Открыть»);
  // иначе или при ошибке — ссылка на страницу-превью (в чате видно фото и название).
  const [sharing, setSharing] = useState(false);
  const share = async () => {
    if (!l) return;
    if (canShareMessage() && !sharing) {
      setSharing(true);
      try {
        const prepared = await api<{ id: string }>(`/listings/${l.id}/share-message`, { method: 'POST' });
        if (await shareMessage(prepared.id)) haptic.success();
        return;
      } catch {
        /* объявление не активно или Telegram недоступен — обычная ссылка */
      } finally {
        setSharing(false);
      }
    }
    shareToTelegram(l.shareUrl, l.title);
  };
  const [contactOpen, setContactOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSellerOpen, setReportSellerOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const action = useListingAction();
  const startChat = useStartChat();
  const tariffs = usePromotionTariffs(Boolean(l?.isOwner && l.status === 'active'));

  /** «Написать продавцу»: открыть (или создать) переписку. Если продавец отключил чат — показать другие способы связи. */
  const writeToSeller = () => {
    if (!l) return;
    startChat.mutate(l.id, {
      onSuccess: (r) => navigate(`/chats/${r.id}`),
      onError: (e) => {
        if (e instanceof ApiError && (e.code === 'CONTACT_UNAVAILABLE' || e.code === 'FEATURE_DISABLED')) setContactOpen(true);
        else toast(errorMessage(t, e), 'error');
      },
    });
  };

  if (isLoading) return <Screen back><Spinner /></Screen>;
  if (error || !l) {
    return (
      <Screen back>
        {error instanceof ApiError && error.code === 'NOT_FOUND' ? (
          <EmptyState icon="search" title={t('listing.notFound')} text={t('listing.notFoundText')} action={<Button onClick={() => navigate('/')}>{t('nav.home')}</Button>} />
        ) : (
          <ErrorState error={error} onRetry={() => void refetch()} />
        )}
      </Screen>
    );
  }

  const run = async (a: 'archive' | 'sold' | 'republish' | 'renew' | 'delete' | 'submit', confirmKey?: string) => {
    if (confirmKey && !(await confirmDialog(t(confirmKey)))) return;
    action.mutate(
      { id: l.id, action: a },
      {
        onSuccess: () => {
          haptic.success();
          toast(t(`listing.done.${a}`), 'success');
          if (a === 'delete') navigate('/profile/listings', { replace: true });
        },
        onError: (e) => {
          haptic.error();
          toast(errorMessage(t, e), 'error');
        },
      },
    );
  };

  const split = splitAttributes(l.attributes, l.condition ? t(`condition.${l.condition}`) : null);

  // «Продлить» — за неделю до окончания срока показа (раньше нет смысла: срок отсчитывается от сегодняшнего дня).
  const expiringSoon = Boolean(l.expiresAt && new Date(l.expiresAt).getTime() - Date.now() < 7 * 86400_000);

  return (
    <Screen back noNav={!l.isOwner}>
      <Gallery
        images={l.images}
        title={l.title}
        categoryId={l.categoryId}
        actions={
          <>
            <button type="button" className="gallery-btn" aria-label={t('listing.share')} onClick={() => void share()}>
              <Icon name="share" size={20} />
            </button>
            {!l.isOwner && l.status === undefined ? <FavoriteButton id={l.id} value={l.isFavorite} overlay /> : null}
          </>
        }
      />
      <div className="detail">
        {l.isOwner && l.status ? (
          <div className={`owner-banner status-${l.status}`}>
            <span className={`status-pill status-${l.status}`}>{t(`status.${l.status}`)}</span>
            {l.status === 'rejected' && l.rejection ? (
              <p>
                <strong>{t('listing.rejectedReason')}:</strong> {t(`rejectReasons.${l.rejection.code ?? 'other'}`)}
                {l.rejection.text ? ` — ${l.rejection.text}` : ''}
              </p>
            ) : null}
            {l.status === 'pending' ? <p className="muted">{t('listing.pendingText')}</p> : null}
            {l.status === 'archived' && l.archiveReason ? <p className="muted">{t(`archiveReasons.${l.archiveReason}`)}</p> : null}
            {l.status === 'active' && l.expiresAt ? <p className="tiny muted">{t('listing.activeUntil', { date: formatDate(l.expiresAt) })}</p> : null}
            {typeof l.viewCount === 'number' ? <p className="tiny muted">{t('listing.views', { n: l.viewCount })}</p> : null}
          </div>
        ) : null}

        <h1 className="detail-title">{l.title}</h1>
        <div className="detail-head">
          <PriceText price={l.price} className="price-lg" />
          {l.price.type === 'negotiable' ? <span className="pill-accent">{t('listing.negotiable')}</span> : null}
        </div>
        <div className="detail-meta">
          {l.location ? (
            <span>
              <Icon name="pin" size={15} /> {shortPlaces(l.location)}
            </span>
          ) : null}
          {l.publishedAt ? <span>{rel(l.publishedAt)}</span> : null}
        </div>
        {l.category ? <div className="crumbs">{l.category.path.map((c) => c.name).join(' › ')}</div> : null}

        <SpecTiles specs={split.specs} />
        {split.tags.length ? (
          <div className="tag-row">
            {split.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <h2 className="detail-h2">{t('listing.description')}</h2>
        <Description text={l.description} />
        <p className="tiny muted">{t('listing.authorLanguageNote')}</p>

        <Link to={`/seller/${l.seller.id}`} className="seller seller-link">
          <Avatar url={l.seller.avatarUrl} name={l.seller.business?.name || l.seller.firstName} />
          <div className="seller-body">
            <div className="seller-name">{l.seller.business?.name ?? (l.seller.firstName || t('listing.seller'))}</div>
            {l.seller.business ? (
              <span className={`status-pill ${l.seller.business.isVerified ? 'status-active' : ''}`}>
                {l.seller.business.isVerified ? t('business.verified') : t('business.profile')}
              </span>
            ) : null}
            <span className="tiny muted seller-since">
              <Icon name="verified" size={14} /> {memberFor(l.seller.memberSince)} · {t('listing.activeListings', { n: l.seller.activeListings })}
            </span>
            <RatingLine rating={l.seller.rating} compact />
            {l.seller.activity ? <span className="tiny muted">{t(`seller.activity.${l.seller.activity}`)}</span> : null}
          </div>
          <Icon name="chevron" size={18} />
        </Link>

        {!l.isOwner ? (
          <button type="button" className="link tiny report-seller" onClick={() => setReportSellerOpen(true)}>
            {t('report.seller')}
          </button>
        ) : null}

        <div className="detail-actions">
          {!l.isOwner ? (
            <Button variant="ghost" size="sm" onClick={() => setReportOpen(true)}>
              <Icon name="flag" size={18} /> {t('listing.report')}
            </Button>
          ) : null}
        </div>

        {l.isOwner ? (
          <div className="owner-actions">
            {['draft', 'rejected', 'active', 'archived', 'pending'].includes(l.status ?? '') ? (
              <Button variant="secondary" block onClick={() => navigate(`/sell/${l.id}`)}>
                <Icon name="edit" size={18} /> {t('listing.edit')}
              </Button>
            ) : null}
            {l.status === 'draft' || l.status === 'rejected' ? (
              <Button block loading={action.isPending} onClick={() => run('submit')}>
                {t('sell.publish')}
              </Button>
            ) : null}
            {l.status === 'active' && tariffs.data?.enabled ? (
              <Button block className="btn-promote" onClick={() => setPromoteOpen(true)}>
                <Icon name="rocket" size={18} /> {t('promote.button')}
              </Button>
            ) : null}
            {l.status === 'active' && expiringSoon ? (
              <Button block onClick={() => run('renew')} loading={action.isPending}>
                <Icon name="check" size={18} /> {t('listing.renew')}
              </Button>
            ) : null}
            {l.status === 'active' ? (
              <>
                <Button block onClick={() => run('sold', 'listing.confirm.sold')} loading={action.isPending}>
                  {t('listing.markSold')}
                </Button>
                <Button variant="secondary" block onClick={() => run('archive', 'listing.confirm.archive')}>
                  {t('listing.archive')}
                </Button>
              </>
            ) : null}
            {l.status === 'archived' && l.archiveReason === 'expired' ? (
              <>
                <Button block onClick={() => run('renew')} loading={action.isPending}>
                  {t('listing.restore')}
                </Button>
                <p className="tiny muted">{t('listing.restoreHint')}</p>
              </>
            ) : l.status === 'archived' ? (
              <Button block onClick={() => run('republish')} loading={action.isPending}>
                {t('listing.republish')}
              </Button>
            ) : null}
            <Button variant="danger" block onClick={() => run('delete', 'listing.confirm.delete')}>
              <Icon name="trash" size={18} /> {t('listing.delete')}
            </Button>
          </div>
        ) : null}

        {similar.data && similar.data.length > 0 ? (
          <section className="section">
            <div className="section-head">
              <h2>{t('listing.similar')}</h2>
            </div>
            <ListingGrid items={similar.data} />
          </section>
        ) : null}
      </div>

      {!l.isOwner ? (
        <div className="sticky-cta sticky-cta-row">
          <Button block size="lg" loading={startChat.isPending} onClick={writeToSeller}>
            <Icon name="chat" size={20} /> {t('chat.write')}
          </Button>
          <Button variant="secondary" size="lg" className="cta-more" onClick={() => setContactOpen(true)} aria-label={t('listing.contact')}>
            <Icon name="phone" size={20} />
          </Button>
        </div>
      ) : null}

      <ContactSheet open={contactOpen} onClose={() => setContactOpen(false)} listing={l} onChat={writeToSeller} />
      {l.isOwner ? <PromoteSheet open={promoteOpen} onClose={() => setPromoteOpen(false)} listingId={l.id} listingTitle={l.title} /> : null}
      <ReportSheet open={reportOpen} onClose={() => setReportOpen(false)} target={{ listingId: l.id }} />
      <ReportSheet open={reportSellerOpen} onClose={() => setReportSellerOpen(false)} target={{ sellerId: l.seller.id }} />
    </Screen>
  );
}

function ContactSheet({ open, onClose, listing, onChat }: { open: boolean; onClose: () => void; listing: ListingDetail; onChat: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useContactOptions(listing.id, open);
  const [message, setMessage] = useState(t('contact.defaultMessage'));
  const [shareUsername, setShareUsername] = useState(Boolean(me.username));
  const [phone, setPhone] = useState(me.contactPreferences.phone ?? '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<'queued' | 'not_deliverable' | null>(null);

  const sendInquiry = async () => {
    setSending(true);
    try {
      const r = await api<{ id: string; status: 'queued' | 'not_deliverable' }>('/contacts/inquiries', {
        method: 'POST',
        json: { listingId: listing.id, message: message.trim() || undefined, shareUsername: shareUsername && Boolean(me.username), phone: phone.trim() || undefined },
      });
      setSent(r.status);
      haptic.success();
    } catch (e) {
      haptic.error();
      toast(errorMessage(t, e), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('contact.title')}>
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.available ? (
        <EmptyState icon="chat" title={t('contact.unavailable')} text={t('contact.unavailableText')} />
      ) : (
        <div className="stack">
          {data.chat ? (
            <Button block onClick={() => { onClose(); onChat(); }}>
              <Icon name="chat" size={18} /> {t('chat.write')}
            </Button>
          ) : null}
          {data.telegramUrl ? (
            <Button block variant={data.chat ? 'secondary' : 'primary'} onClick={() => openTelegramLink(data.telegramUrl!)}>
              <Icon name="send" size={18} /> {t('contact.openTelegram')}
            </Button>
          ) : null}
          {data.phone ? (
            <a className="btn btn-secondary btn-md btn-block" href={`tel:${data.phone}`}>
              <Icon name="phone" size={18} /> {formatUzPhone(data.phone)}
            </a>
          ) : null}
          {data.botInquiry ? (
            sent ? (
              <div className="notice notice-success">{sent === 'queued' ? t('contact.inquiryQueued') : t('contact.inquiryNotDeliverable')}</div>
            ) : (
              <div className="inquiry">
                <h3>{t('contact.viaBot')}</h3>
                <p className="tiny muted">{t('contact.viaBotHint')}</p>
                <Field label={t('contact.message')}>
                  <textarea className="input" rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} />
                </Field>
                {me.username ? (
                  <label className="check-row">
                    <input type="checkbox" checked={shareUsername} onChange={(e) => setShareUsername(e.target.checked)} />
                    <span>{t('contact.shareUsername', { username: me.username })}</span>
                  </label>
                ) : null}
                <Field label={t('contact.yourPhone')} hint={me.username ? t('contact.phoneOptional') : t('contact.phoneRequired')}>
                  <input className="input" inputMode="tel" placeholder="+998 90 123 45 67" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Field>
                <Button block loading={sending} disabled={!(shareUsername && me.username) && !phone.trim()} onClick={sendInquiry}>
                  {t('contact.send')}
                </Button>
              </div>
            )
          ) : null}
          <p className="tiny muted">{t('contact.safety')}</p>
        </div>
      )}
    </Sheet>
  );
}

/** Описание: длинное сворачивается до 6 строк с кнопкой «Читать полностью». */
function Description({ text }: { text: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const long = text.length > 280 || text.split('\n').length > 6;
  return (
    <>
      <p className={`description ${long && !open ? 'clamped' : ''}`}>{text}</p>
      {long && !open ? (
        <button type="button" className="link read-more" onClick={() => setOpen(true)}>
          {t('listing.readMore')}
        </button>
      ) : null}
    </>
  );
}
